import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Mail } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { devWarn } from '../../lib/log';

export const SettingsTab = ({ authAxios }) => {
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [testEmailTo, setTestEmailTo] = useState('hello@melopainfra.com');
  const [testingEmail, setTestingEmail] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      const res = await authAxios.get('/admin/settings');
      setSettings(res.data);
    } catch (e) {
      // 401 is redirected by the interceptor; log anything else.
      if (e?.response?.status !== 401) devWarn('Settings load failed:', e);
    }
  }, [authAxios]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const setS = (k) => (v) => setSettings((s) => ({ ...s, [k]: v }));

  const saveSettings = async (keys) => {
    setSavingSettings(true);
    try {
      const payload = {};
      keys.forEach((k) => {
        if (k === 'welcome_email_enabled') {
          payload[k] = settings[k] !== false && String(settings[k]) !== 'false';
        } else {
          payload[k] = settings[k] ?? '';
        }
      });
      const res = await authAxios.put('/admin/settings', payload);
      setSettings(res.data);
      toast.success('Settings saved');
    } catch (e) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const settingsField = (key, label, type = 'text', placeholder = '') => (
    <div>
      <label className="text-sm font-medium" htmlFor={`set-${key}`}>{label}</label>
      <Input
        id={`set-${key}`}
        type={type}
        value={settings?.[key] ?? ''}
        onChange={(e) => setS(key)(e.target.value)}
        placeholder={placeholder}
        className="h-10 mt-1.5"
        data-testid={`admin-settings-${key.replace(/_/g, '-')}-input`}
      />
    </div>
  );

  if (!settings) {
    return (
      <div className="p-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
    );
  }

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="font-display font-semibold text-lg">Analytics & Tracking</h3>
        <p className="text-sm text-muted-foreground mt-1">Scripts load automatically on the website once IDs are saved.</p>
        <div className="mt-5 space-y-4">
          {settingsField('ga4_id', 'Google Analytics 4 ID', 'text', 'G-XXXXXXXXXX')}
          {settingsField('gtm_id', 'Google Tag Manager ID', 'text', 'GTM-XXXXXXX')}
          {settingsField('meta_pixel_id', 'Meta Pixel ID', 'text', '1234567890')}
          {settingsField('google_ads_id', 'Google Ads Conversion ID', 'text', 'AW-XXXXXXXXX')}
          <Button
            className="h-10"
            disabled={savingSettings}
            onClick={() => saveSettings(['ga4_id', 'gtm_id', 'meta_pixel_id', 'google_ads_id'])}
            data-testid="admin-settings-analytics-save-button"
          >
            {savingSettings ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save Analytics
          </Button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="font-display font-semibold text-lg">Email Notifications (SMTP)</h3>
        <p className="text-sm text-muted-foreground mt-1">
          When configured, every new lead is emailed to the notification address. Leave blank to disable.
        </p>
        <div className="mt-5 space-y-4">
          {settingsField('smtp_host', 'SMTP Host (Zoho: smtp.zoho.com / smtp.zoho.in)', 'text', 'smtp.zoho.com')}
          <div className="grid grid-cols-2 gap-4">
            {settingsField('smtp_port', 'Port', 'text', '587')}
            {settingsField('smtp_from', 'From Address', 'text', 'no-reply@melopainfra.com')}
          </div>
          {settingsField('smtp_user', 'SMTP Username', 'text')}
          {settingsField('smtp_password', 'SMTP Password', 'password')}
          {settingsField('notification_email', 'Notification Email', 'email', 'hello@melopainfra.com')}
          <label className="flex items-start gap-3 text-sm cursor-pointer select-none pt-1" htmlFor="welcome-email-toggle">
            <Checkbox
              id="welcome-email-toggle"
              checked={settings.welcome_email_enabled !== false && String(settings.welcome_email_enabled) !== 'false'}
              onCheckedChange={(v) => setS('welcome_email_enabled')(!!v)}
              data-testid="admin-settings-welcome-email-toggle"
              className="mt-0.5"
            />
            <span>
              <span className="font-medium block">Auto welcome email</span>
              <span className="text-muted-foreground text-xs block mt-0.5">
                Instantly sends a branded welcome email to every new enquiry that shares an email address.
              </span>
            </span>
          </label>
          <Button
            className="h-10"
            disabled={savingSettings}
            onClick={() => saveSettings(['smtp_host', 'smtp_port', 'smtp_user', 'smtp_password', 'smtp_from', 'notification_email', 'welcome_email_enabled'])}
            data-testid="admin-settings-smtp-save-button"
          >
            {savingSettings ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save SMTP
          </Button>
          <div className="pt-4 mt-2 border-t border-border">
            <label className="text-sm font-medium">Verify configuration — send a test email</label>
            <div className="mt-1.5 flex gap-2">
              <Input
                type="email"
                value={testEmailTo}
                onChange={(e) => setTestEmailTo(e.target.value)}
                className="h-10 flex-1"
                data-testid="admin-settings-test-email-input"
              />
              <Button
                variant="outline"
                className="h-10 shrink-0"
                disabled={testingEmail || !testEmailTo.trim()}
                data-testid="admin-settings-test-email-button"
                onClick={async () => {
                  setTestingEmail(true);
                  try {
                    const res = await authAxios.post('/admin/settings/test-email', { to: testEmailTo.trim() }, { timeout: 60000 });
                    toast.success(`Test email sent to ${res.data.to} — check the inbox`);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Test email failed');
                  } finally {
                    setTestingEmail(false);
                  }
                }}
              >
                {testingEmail ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                Send Test
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Save your Zoho App Password first, then send a test. Zoho: smtppro.zoho.in, port 465 (SSL) or 587 (TLS).
            </p>
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="font-display font-semibold text-lg">Contact Information</h3>
        <div className="mt-5 space-y-4">
          {settingsField('contact_email', 'Public Email', 'email')}
          {settingsField('contact_phone', 'Public Phone', 'text')}
          {settingsField('whatsapp_number', 'WhatsApp Number (digits only)', 'text', '917975127454')}
          <Button
            className="h-10"
            disabled={savingSettings}
            onClick={() => saveSettings(['contact_email', 'contact_phone', 'whatsapp_number'])}
            data-testid="admin-settings-contact-save-button"
          >
            {savingSettings ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save Contact
          </Button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="font-display font-semibold text-lg">Social Media Links</h3>
        <p className="text-sm text-muted-foreground mt-1">Icons appear in the website footer only for links you fill in.</p>
        <div className="mt-5 space-y-4">
          {settingsField('social_instagram', 'Instagram URL', 'url', 'https://instagram.com/...')}
          {settingsField('social_facebook', 'Facebook URL', 'url', 'https://facebook.com/...')}
          {settingsField('social_linkedin', 'LinkedIn URL', 'url', 'https://linkedin.com/company/...')}
          {settingsField('social_youtube', 'YouTube Channel URL', 'url', 'https://youtube.com/@...')}
          {settingsField('social_x', 'X (Twitter) URL', 'url', 'https://x.com/...')}
          <Button
            className="h-10"
            disabled={savingSettings}
            onClick={() => saveSettings(['social_instagram', 'social_facebook', 'social_linkedin', 'social_youtube', 'social_x'])}
            data-testid="admin-settings-social-save-button"
          >
            {savingSettings ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save Social Links
          </Button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="font-display font-semibold text-lg">Business Details</h3>
        <p className="text-sm text-muted-foreground mt-1">Shown on quotations sent to customers.</p>
        <div className="mt-5 space-y-4">
          {settingsField('gstin', 'GSTIN', 'text', '29XXXXX0000X1Z5')}
          {settingsField('company_address', 'Company Address', 'text', 'Registered office address')}
          <Button
            className="h-10"
            disabled={savingSettings}
            onClick={() => saveSettings(['gstin', 'company_address'])}
            data-testid="admin-settings-business-save-button"
          >
            {savingSettings ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save Business Details
          </Button>
        </div>
      </div>
    </div>
  );
};
